import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Compass, MessageCircle, ArrowLeft, Calendar } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast.error("Failed to load conversations");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getConversationPreview = (messages) => {
    if (!messages || messages.length === 0) return "No messages";
    const lastUserMessage = [...messages].reverse().find(m => m.role === "user");
    return lastUserMessage?.content.slice(0, 100) + (lastUserMessage?.content.length > 100 ? "..." : "") || "New conversation";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-home-button"
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-primary hover:bg-primary/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <img 
                  src="/asher-avatar.png" 
                  alt="Asher" 
                  className="w-11 h-11 rounded-full object-cover shadow-md ring-2 ring-primary/20"
                />
                <h1 className="text-xl font-heading font-bold text-primary">My Conversations</h1>
              </div>
            </div>
            <Button
              data-testid="new-conversation-button"
              onClick={() => navigate("/chat")}
              className="min-h-[48px] px-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
            >
              New Conversation
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-lg text-text-secondary font-body">Loading conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-12 text-center">
            <MessageCircle className="w-16 h-16 text-muted mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary mb-3">
              No Conversations Yet
            </h2>
            <p className="text-base text-text-secondary font-body mb-6">
              Start a conversation with Asher to get personalized care guidance.
            </p>
            <Button
              data-testid="start-first-conversation-button"
              onClick={() => navigate("/chat")}
              className="min-h-[48px] px-8 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
            >
              Start Your First Conversation
            </Button>
          </div>
        ) : (
          <div data-testid="conversations-list" className="space-y-4">
            {conversations.map((conversation) => (
              <div
                key={conversation.session_id}
                data-testid={`conversation-${conversation.session_id}`}
                onClick={() => navigate(`/chat/${conversation.session_id}`)}
                className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-all duration-300 p-6 cursor-pointer border-l-4 border-l-secondary"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <MessageCircle className="w-5 h-5 text-primary" />
                      <h3 className="text-lg font-heading font-semibold text-text-primary">
                        {conversation.care_type_recommendation
                          ? `${conversation.care_type_recommendation} Consultation`
                          : "Care Consultation"}
                      </h3>
                    </div>
                    <p className="text-sm text-text-secondary font-body mb-3">
                      {getConversationPreview(conversation.messages)}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-text-secondary">
                      <Calendar className="w-4 h-4" />
                      <span className="font-body">{formatDate(conversation.updated_at)}</span>
                      <span className="mx-2">•</span>
                      <span className="font-body">
                        {conversation.messages.length} message{conversation.messages.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  {conversation.care_type_recommendation && (
                    <span className="px-3 py-1 bg-accent/10 text-accent text-sm font-body font-semibold rounded-full">
                      {conversation.care_type_recommendation}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
